import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Star, MessageSquare, User, Calendar, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function VisioRatings() {
  const navigate = useNavigate();
  const [ratings, setRatings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('kardif_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [ratingsRes, statsRes] = await Promise.all([
        axios.get(`${API}/visio/ratings`, { headers }),
        axios.get(`${API}/visio/ratings/stats`, { headers })
      ]);
      
      setRatings(ratingsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast.error('Erreur lors du chargement des évaluations');
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-5 w-5 ${rating >= star ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#8B7355]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1A1A1A] p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-white">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold text-white">Évaluations Visio-Assistance</h1>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="bg-[#2A2A2A] border-[#3A3A3A]">
              <CardContent className="p-4 text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Star className="h-6 w-6 text-yellow-400 fill-yellow-400" />
                  <span className="text-3xl font-bold text-white">{stats.average_rating}</span>
                </div>
                <p className="text-gray-400 text-sm">Note moyenne</p>
              </CardContent>
            </Card>
            
            <Card className="bg-[#2A2A2A] border-[#3A3A3A]">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-white mb-2">{stats.total_ratings}</div>
                <p className="text-gray-400 text-sm">Total évaluations</p>
              </CardContent>
            </Card>
            
            <Card className="bg-[#2A2A2A] border-[#3A3A3A]">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-green-400 mb-2">{stats.rating_5 + stats.rating_4}</div>
                <p className="text-gray-400 text-sm">Satisfaits (4-5⭐)</p>
              </CardContent>
            </Card>
            
            <Card className="bg-[#2A2A2A] border-[#3A3A3A]">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-red-400 mb-2">{stats.rating_1 + stats.rating_2}</div>
                <p className="text-gray-400 text-sm">Insatisfaits (1-2⭐)</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Rating Distribution */}
        {stats && stats.total_ratings > 0 && (
          <Card className="bg-[#2A2A2A] border-[#3A3A3A] mb-6">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Distribution des notes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[5, 4, 3, 2, 1].map((star) => {
                  const count = stats[`rating_${star}`];
                  const percentage = stats.total_ratings > 0 ? (count / stats.total_ratings) * 100 : 0;
                  return (
                    <div key={star} className="flex items-center gap-3">
                      <div className="flex items-center gap-1 w-16">
                        <span className="text-white">{star}</span>
                        <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                      </div>
                      <div className="flex-1 bg-[#3A3A3A] rounded-full h-4 overflow-hidden">
                        <div 
                          className="h-full bg-yellow-400 transition-all duration-500"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="text-gray-400 text-sm w-12 text-right">{count}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Ratings List */}
        <Card className="bg-[#2A2A2A] border-[#3A3A3A]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Commentaires récents
            </CardTitle>
          </CardHeader>
          <CardContent>
            {ratings.length === 0 ? (
              <p className="text-gray-400 text-center py-8">Aucune évaluation pour le moment</p>
            ) : (
              <div className="space-y-4">
                {ratings.map((rating, index) => (
                  <div key={index} className="bg-[#1A1A1A] rounded-lg p-4 border border-[#3A3A3A]">
                    <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#8B7355] flex items-center justify-center">
                          <User className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <p className="text-white font-medium">{rating.client_name || 'Client'}</p>
                          <p className="text-gray-400 text-sm">{rating.client_phone || '-'}</p>
                        </div>
                      </div>
                      {renderStars(rating.client_rating)}
                    </div>
                    
                    {rating.client_feedback && (
                      <p className="text-gray-300 bg-[#2A2A2A] rounded p-3 mb-3 italic">
                        "{rating.client_feedback}"
                      </p>
                    )}
                    
                    <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                      <div className="flex items-center gap-1">
                        <User className="h-4 w-4" />
                        <span>Tech: {rating.technician_name || '-'}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        <span>{formatDate(rating.rated_at)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
